﻿import {translation} from "@2d-front-monorepo/core";
import {Message, PopOver, showErrorDialog, TabPanel, Tabs, toggleLoadingDialog} from "@2d-front-monorepo/design-library";
import {
	VehiclePropertyFlags,
	VehicleResult,
	VehicleType
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, useMemo} from "react";
import {Link} from "react-router";
import MaterialIcon from "../../components/MaterialIcon";
import {VehicleComponent} from "../../components/VehicleComponent";
import {getGlobalContext} from "../../hooks/getGlobalContext";
import {ReloadEntities, reloadPermits} from "../../hooks/reloadPermits";
import {usePermits} from "../../hooks/usePermits";
import {formatRoute, PrivateRoutes} from "../../routes";
import {getHttpClient} from "../../hooks/getHttpClient";

async function toggleVisibility(vehicle: VehicleResult): Promise<void> {
	if (!confirm(translation(PermitTranslations.vehicles_page__remove_vehicle, {plate: vehicle.plate}))) {
		return;
	}

	try {
		toggleLoadingDialog(true);
		await getHttpClient().vehicles.hide(vehicle.id);
		await reloadPermits(ReloadEntities.Holder);
	} catch (error) {
		showErrorDialog(error);
	} finally {
		toggleLoadingDialog(false);
	}
}

function VehicleCard({vehicle, editable}: { vehicle: VehicleResult, editable: boolean }): JSX.Element {
	return <div className="card layout horizontal aligned top">
		<div className="flex">
			<VehicleComponent vehicle={vehicle}/>
		</div>

		<div className="padded block start">
			<PopOver>
				{editable &&
                    <Link to={formatRoute(PrivateRoutes.vehicleEdit, vehicle.id)}>
                        <MaterialIcon icon="edit"/>
						{translation(PermitTranslations.edit)}
                    </Link>
				}
				<button onClick={() => toggleVisibility(vehicle)} type={"button"}>
					<MaterialIcon icon={"delete"}/>
					{translation(PermitTranslations.delete)}
				</button>
			</PopOver>
		</div>

	</div>
}

function VehicleList({vehicles, editable}: { vehicles: VehicleResult[], editable: boolean }): JSX.Element {
	return <div className="layout vertical gap">
		{vehicles.map(vehicle => <VehicleCard vehicle={vehicle} editable={editable} key={vehicle.id}/>)}
	</div>;
}

const EDITABLE_FIELDS = VehiclePropertyFlags.Province | VehiclePropertyFlags.Color | VehiclePropertyFlags.Model | VehiclePropertyFlags.Nickname | VehiclePropertyFlags.Type;

export default function Vehicles(): JSX.Element {
	const {installation} = getGlobalContext();
	const permitsState = usePermits();
	const residentVehicles = useMemo(() => permitsState.vehicles.filter(x => x.type === VehicleType.Resident && !x.hidden), [permitsState.vehicles]);
	const visitorVehicles = useMemo(() => permitsState.vehicles.filter(x => x.type === VehicleType.Visitor && !x.hidden), [permitsState.vehicles]);
	const residentHidden = residentVehicles.length === 0;
	const visitorHidden = visitorVehicles.length === 0;

	const noFieldsVisible = (installation.configuration.vehicles.defaultRules.hidden & EDITABLE_FIELDS) === EDITABLE_FIELDS;

	if (residentHidden && visitorHidden) {
		return <Message title={{text: translation(PermitTranslations.vehicles_page__no_vehicles)}} type="information"/>
	}

	return (
		<>
			<h1>{translation(PermitTranslations.vehicles)}</h1>
			<Tabs>
				<TabPanel title={translation(PermitTranslations.vehicles_page__residents)} hidden={residentHidden}>
					<VehicleList vehicles={residentVehicles} editable={!noFieldsVisible}/>
				</TabPanel>
				<TabPanel title={translation(PermitTranslations.vehicles_page__visitors)} hidden={visitorHidden}>
					<VehicleList vehicles={visitorVehicles} editable={!noFieldsVisible}/>
				</TabPanel>
			</Tabs>
		</>
	)
}
